import React, { useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  BeakerIcon,
  CubeIcon,
  SparklesIcon,
  LightBulbIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon as SparklesIconSolid
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesIconFilled } from '@heroicons/react/24/solid';
import { FeedStockNutritionalInfo, FeedType, FeedStockUnit } from '../api/feedStock';
import { feedStockApi } from '../api/feedStock';
import { toast } from 'react-hot-toast';
import NutritionalInfoDecimalInput from './NutritionalInfoDecimalInput';

interface NutritionalInfoFormProps {
  nutritionalInfo: FeedStockNutritionalInfo;
  onChange: (field: keyof FeedStockNutritionalInfo, value: any) => void;
  errors?: Record<string, string>;
  className?: string;
  feedName?: string;
  feedType?: FeedType;
  unit?: FeedStockUnit;
  description?: string;
}

function NutritionalNumberField({
  label,
  field,
  unit,
  nutritionalInfo,
  onChange,
  errors,
  min,
  max,
  placeholder,
  className,
}: {
  label: string;
  field: keyof FeedStockNutritionalInfo;
  unit: string;
  nutritionalInfo: FeedStockNutritionalInfo;
  onChange: (field: keyof FeedStockNutritionalInfo, value: any) => void;
  errors?: Record<string, string>;
  min?: number;
  max?: number;
  placeholder?: string;
  className?: string;
}) {
  return (
    <NutritionalInfoDecimalInput
      label={label}
      field={field}
      unit={unit}
      value={nutritionalInfo[field] as number | undefined}
      onChange={onChange}
      min={min}
      max={max}
      placeholder={placeholder}
      className={className}
      error={errors?.[field]}
    />
  );
}

export default function NutritionalInfoForm({
  nutritionalInfo,
  onChange,
  errors = {},
  className = '',
  feedName = '',
  feedType,
  unit,
  description = ''
}: NutritionalInfoFormProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['macronutrients']));
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuccess, setAiSuccess] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getCompletionPercentage = (section: string): number => {
    const sectionFields = {
      macronutrients: ['protein', 'fat', 'fiber', 'carbohydrate', 'moisture', 'calories'],
      majorMinerals: ['calcium', 'phosphorus', 'magnesium', 'potassium', 'sodium'],
      traceMinerals: ['iron', 'zinc', 'manganese', 'copper', 'selenium'],
      fatSolubleVitamins: ['vitaminA', 'vitaminD3', 'vitaminE', 'vitaminK'],
      waterSolubleVitamins: ['thiamine', 'riboflavin', 'niacin', 'pantothenicAcid', 'pyridoxine', 'biotin', 'folicAcid', 'vitaminB12', 'choline']
    };

    const fields = sectionFields[section as keyof typeof sectionFields] || [];
    const completedFields = fields.filter(field => 
      nutritionalInfo[field as keyof FeedStockNutritionalInfo] !== undefined &&
      nutritionalInfo[field as keyof FeedStockNutritionalInfo] !== null &&
      nutritionalInfo[field as keyof FeedStockNutritionalInfo] !== ''
    );

    return fields.length > 0 ? (completedFields.length / fields.length) * 100 : 0;
  };

  const getSectionIcon = (section: string) => {
    switch (section) {
      case 'macronutrients':
        return <BeakerIcon className="h-5 w-5" />;
      case 'majorMinerals':
        return <CubeIcon className="h-5 w-5" />;
      case 'traceMinerals':
        return <SparklesIcon className="h-5 w-5" />;
      case 'fatSolubleVitamins':
      case 'waterSolubleVitamins':
        return <LightBulbIcon className="h-5 w-5" />;
      default:
        return <InformationCircleIcon className="h-5 w-5" />;
    }
  };

  const getCompletionColor = (percentage: number): string => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompletionBadge = (percentage: number): JSX.Element => {
    if (percentage >= 80) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Complete
        </span>
      );
    }
    if (percentage >= 50) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          Partial
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
        <InformationCircleIcon className="h-3 w-3 mr-1" />
        Limited
      </span>
    );
  };

  const handleAIAutoComplete = async () => {
    if (!feedName.trim() || !feedType || !unit) {
      setAiError('Please provide feed name, type, and unit before using AI auto-complete.');
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiSuccess(null);

    try {
      const response = await feedStockApi.getAINutritionalInfo({
        feedName: feedName.trim(),
        feedType,
        unit,
        description
      });

      if (response.data.success && response.data.nutritionalInfo) {
        // Apply the AI-generated nutritional information
        const aiNutritionalInfo = response.data.nutritionalInfo;
        
        // Update all nutritional fields with AI data
        Object.keys(aiNutritionalInfo).forEach(key => {
          const value = aiNutritionalInfo[key as keyof FeedStockNutritionalInfo];
          if (value !== undefined && value !== null) {
            onChange(key as keyof FeedStockNutritionalInfo, value);
          }
        });

        const confidence = response.data.confidence;
        const validation = response.data.validation;
        
        if (confidence >= 70) {
          setAiSuccess(`AI analysis completed with ${confidence}% confidence. Nutritional data has been auto-filled.`);
          toast.success(`AI nutritional analysis completed (${confidence}% confidence)`);
        } else {
          setAiSuccess(`AI analysis completed with ${confidence}% confidence. Please review the data before saving.`);
          toast(`AI analysis completed with low confidence (${confidence}%). Please review.`, {
            icon: '⚠️',
            style: {
              background: '#fef3c7',
              color: '#92400e',
            },
          });
        }

        // Show validation warnings if any
        if (validation?.warnings?.length) {
        }

        if (validation?.issues?.length) {
          console.error('AI Nutritional Validation Issues:', validation.issues);
          setAiError(`AI analysis completed but with validation issues: ${validation.issues.join(', ')}`);
        }

      } else {
        setAiError(response.data.error || 'AI analysis failed. Please try again.');
        toast.error('AI nutritional analysis failed');
      }

    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'AI analysis failed';
      setAiError(errorMessage);
      toast.error('AI nutritional analysis failed');
    } finally {
      setAiLoading(false);
    }
  };

  const NutritionalSection = ({
    title, 
    sectionKey, 
    children 
  }: { 
    title: string; 
    sectionKey: string; 
    children: React.ReactNode;
  }) => {
    const isExpanded = expandedSections.has(sectionKey);
    const completion = getCompletionPercentage(sectionKey);
    
    return (
      <div className="border border-gray-200 rounded-lg shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-inset rounded-t-lg"
        >
          <div className="flex items-center space-x-3">
            <div className="text-cyan-600">
              {getSectionIcon(sectionKey)}
            </div>
            <div>
              <h3 className="text-base font-medium text-slate-900">{title}</h3>
              <div className="flex items-center mt-1 space-x-2">
                <span className="text-sm text-slate-500">
                  {completion.toFixed(0)}% complete
                </span>
                {getCompletionBadge(completion)}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  completion >= 80 ? 'bg-green-500' : 
                  completion >= 50 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}
                style={{ width: `${completion}%` }}
              />
            </div>
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-slate-400" />
            )}
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* AI Auto-Complete Section */}
      <div className="border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <SparklesIconFilled className="h-5 w-5 text-purple-600" />
            <h3 className="text-base font-medium text-slate-900">AI Nutritional Analysis</h3>
          </div>
          <button
            type="button"
            onClick={handleAIAutoComplete}
            disabled={aiLoading || !feedName.trim() || !feedType || !unit}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              aiLoading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500'
            }`}
          >
            {aiLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analyzing...
              </>
            ) : (
              <>
                <SparklesIconFilled className="h-4 w-4 mr-2" />
                AI Auto-Complete
              </>
            )}
          </button>
        </div>
        
        <div className="text-sm text-slate-600 mb-3">
          Use AI to automatically generate nutritional information based on the feed type and name. 
          This will analyze typical nutritional profiles for {feedType?.toLowerCase() || 'this type of'} feed.
        </div>

        {aiError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <div className="text-sm text-red-800">{aiError}</div>
            </div>
          </div>
        )}

        {aiSuccess && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
              <div className="text-sm text-green-800">{aiSuccess}</div>
            </div>
          </div>
        )}

        {!feedName.trim() || !feedType || !unit ? (
          <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
            <InformationCircleIcon className="h-4 w-4 inline mr-1" />
            Please provide feed name, type, and unit to enable AI auto-complete.
          </div>
        ) : null}
      </div>

      {/* Macronutrients */}
      <NutritionalSection title="Macronutrients" sectionKey="macronutrients">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Protein"
            field="protein"
            unit="g/kg"
            max={1000}
            placeholder="e.g., 180"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Fat"
            field="fat"
            unit="g/kg"
            max={1000}
            placeholder="e.g., 80"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Fiber"
            field="fiber"
            unit="g/kg"
            max={1000}
            placeholder="e.g., 45"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Carbohydrate"
            field="carbohydrate"
            unit="g/kg"
            max={1000}
            placeholder="e.g., 250"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Moisture"
            field="moisture"
            unit="g/kg"
            max={1000}
            placeholder="e.g., 120"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Calories"
            field="calories"
            unit="kcal/kg"
            max={5000}
            placeholder="e.g., 3200"
          />
        </div>
      </NutritionalSection>

      {/* Major Minerals */}
      <NutritionalSection title="Major Minerals" sectionKey="majorMinerals">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Calcium"
            field="calcium"
            unit="g/kg"
            max={50}
            placeholder="e.g., 8.0"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Phosphorus"
            field="phosphorus"
            unit="g/kg"
            max={50}
            placeholder="e.g., 6.0"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Magnesium"
            field="magnesium"
            unit="g/kg"
            max={10}
            placeholder="e.g., 1.5"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Potassium"
            field="potassium"
            unit="g/kg"
            max={50}
            placeholder="e.g., 4.0"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Sodium"
            field="sodium"
            unit="g/kg"
            max={10}
            placeholder="e.g., 1.5"
          />
        </div>
      </NutritionalSection>

      {/* Trace Minerals */}
      <NutritionalSection title="Trace Minerals" sectionKey="traceMinerals">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Iron"
            field="iron"
            unit="mg/kg"
            max={1000}
            placeholder="e.g., 80"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Zinc"
            field="zinc"
            unit="mg/kg"
            max={1000}
            placeholder="e.g., 65"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Manganese"
            field="manganese"
            unit="mg/kg"
            max={1000}
            placeholder="e.g., 75"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Copper"
            field="copper"
            unit="mg/kg"
            max={100}
            placeholder="e.g., 8"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Selenium"
            field="selenium"
            unit="mg/kg"
            max={10}
            placeholder="e.g., 0.3"
          />
        </div>
      </NutritionalSection>

      {/* Fat-Soluble Vitamins */}
      <NutritionalSection title="Fat-Soluble Vitamins" sectionKey="fatSolubleVitamins">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Vitamin A"
            field="vitaminA"
            unit="IU/kg"
            max={100000}
            placeholder="e.g., 8000"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Vitamin D3"
            field="vitaminD3"
            unit="IU/kg"
            max={10000}
            placeholder="e.g., 1500"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Vitamin E"
            field="vitaminE"
            unit="IU/kg"
            max={200}
            placeholder="e.g., 75"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Vitamin K"
            field="vitaminK"
            unit="mg/kg"
            max={100}
            placeholder="e.g., 2"
          />
        </div>
      </NutritionalSection>

      {/* Water-Soluble Vitamins */}
      <NutritionalSection title="Water-Soluble Vitamins" sectionKey="waterSolubleVitamins">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Thiamine (B1)"
            field="thiamine"
            unit="mg/kg"
            max={100}
            placeholder="e.g., 2"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Riboflavin (B2)"
            field="riboflavin"
            unit="mg/kg"
            max={100}
            placeholder="e.g., 4"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Niacin (B3)"
            field="niacin"
            unit="mg/kg"
            max={200}
            placeholder="e.g., 35"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Pantothenic Acid (B5)"
            field="pantothenicAcid"
            unit="mg/kg"
            max={100}
            placeholder="e.g., 10"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Pyridoxine (B6)"
            field="pyridoxine"
            unit="mg/kg"
            max={100}
            placeholder="e.g., 3"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Biotin (B7)"
            field="biotin"
            unit="mg/kg"
            max={10}
            placeholder="e.g., 0.15"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Folic Acid (B9)"
            field="folicAcid"
            unit="mg/kg"
            max={10}
            placeholder="e.g., 0.55"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Vitamin B12"
            field="vitaminB12"
            unit="mcg/kg"
            max={1000}
            placeholder="e.g., 12"
          />
          <NutritionalNumberField nutritionalInfo={nutritionalInfo} onChange={onChange} errors={errors}
            label="Choline"
            field="choline"
            unit="mg/kg"
            max={2000}
            placeholder="e.g., 1200"
          />
        </div>
      </NutritionalSection>

      {/* Analysis Information */}
      <div className="border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-base font-medium text-slate-900 mb-4 flex items-center">
          <InformationCircleIcon className="h-5 w-5 text-cyan-600 mr-2" />
          Analysis Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Guaranteed Analysis
            </label>
            <select
              className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              value={nutritionalInfo.guaranteed || ''}
              onChange={(e) => onChange('guaranteed', e.target.value || undefined)}
            >
              <option key="empty-option-1753461297333" value="">Select...</option>
              <option key="yes-1753461297333" value="yes">Yes</option>
              <option key="no-1753461297333" value="no">No</option>
              <option key="partial-1753461297333" value="partial">Partial</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Analysis Date
            </label>
            <input
              type="date"
              className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              value={nutritionalInfo.analysisDate || ''}
              onChange={(e) => onChange('analysisDate', e.target.value || undefined)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Analysis Method
            </label>
            <input
              type="text"
              className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              value={nutritionalInfo.analysisMethod || ''}
              onChange={(e) => onChange('analysisMethod', e.target.value || undefined)}
              placeholder="e.g., AOAC, NIR, Chemical"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Certified By
            </label>
            <input
              type="text"
              className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              value={nutritionalInfo.certifiedBy || ''}
              onChange={(e) => onChange('certifiedBy', e.target.value || undefined)}
              placeholder="e.g., Laboratory name or certification body"
            />
          </div>
        </div>
      </div>
    </div>
  );
} 